import React, { useState, useEffect, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../contexts/AuthContext';
import { exportTicketsToExcel, generateExportFilename } from '../../utils/excelExport';
import { getStatusDisplayName, getStatusColorClasses, isTicketClosed, isTicketOpen } from '../../utils/statusUtils';

interface ServiceTicket {
  id: string;
  ticketNumber: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  category: string;
  customerId: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  customerCompany?: string;
  customerAddress?: string;
  customerStreetAddress?: string;
  customerCity?: string;
  customerState?: string;
  customerZipCode?: string;
  customerCountry?: string;
  customerType?: string;
  deviceModel?: string;
  deviceSerialNumber?: string;
  agentId?: string;
  createdAt: string;
  updatedAt: string;
  serviceWorkflows: any[];
  hasServiceWorkflow: boolean;
  messages?: any[];
  agent?: any;
  customer?: any;
}

interface Column {
  id: string;
  label: string;
  width: number;
  visible: boolean;
  resizable: boolean;
  sortable?: boolean;
  filterable?: boolean;
  filterType?: 'text' | 'select' | 'date' | 'number';
  filterOptions?: Array<{ value: string; label: string }>;
  render: (ticket: ServiceTicket) => React.ReactNode;
}

interface ColumnFilter {
  columnId: string;
  value: string;
  operator?: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'greaterThan' | 'lessThan';
}

interface TablePreferences {
  columnOrder: string[];
  columnWidths: Record<string, number>;
  columnVisibility: Record<string, boolean>;
  pagination?: {
    itemsPerPage: number;
    currentPage: number;
  };
}

interface ConfigurableServiceTableProps {
  tickets: ServiceTicket[];
  onTicketSelect: (ticket: ServiceTicket) => void;
  onDeleteTicket?: (ticketId: string) => void;
  onReassignTicket?: (ticketId: string, newAgentId: string) => void;
  getStatusColor: (status: string) => string;
  getPriorityColor: (priority: string) => string;
  formatDate: (dateString: string) => string;
  formatRelativeTime: (dateString: string) => string;
  getLastCustomerMessage: (ticket: ServiceTicket) => any;
  getLastAgentMessage: (ticket: ServiceTicket) => any;
  formatStatus: (status: string) => string;
  getRowStyle: (ticketId: string) => React.CSSProperties;
  agents: any[];
  agentPresence?: Map<string, boolean>;
  reassignTicketId: string | null;
  setReassignTicketId: (id: string | null) => void;
  reassigning: boolean;
  deletingTicketId: string | null;
  exportFilters?: {
    searchTerm?: string;
    statusFilter?: string;
    priorityFilter?: string;
    currentView?: string;
  };
}

const STORAGE_KEY = 'agent-service-table-preferences';

// Helper function to get service workflow info
const getServiceWorkflowInfo = (ticket: ServiceTicket) => {
  const workflows = ticket.serviceWorkflows || [];
  if (workflows.length === 0) return null;

  const activeWorkflow = workflows.find((w: any) => w.status === 'in_progress') || workflows[0];
  return {
    workflowNumber: activeWorkflow.workflowNumber || activeWorkflow.workflow_number,
    currentStep: activeWorkflow.currentStep || activeWorkflow.current_step,
    status: activeWorkflow.status,
    deviceSerialNumber: activeWorkflow.deviceSerialNumber || activeWorkflow.device_serial_number,
    initiatedAt: activeWorkflow.initiatedAt || activeWorkflow.initiated_at,
    completedAt: activeWorkflow.completedAt || activeWorkflow.completed_at,
    cancelledAt: activeWorkflow.cancelledAt || activeWorkflow.cancelled_at,
    initiatedBy: activeWorkflow.initiatedBy || activeWorkflow.initiated_by
  };
};

const getWorkflowStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'in_progress':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'cancelled':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
};

const ColumnFilterDropdown: React.FC<{
  column: Column;
  filter: ColumnFilter | undefined;
  onFilterChange: (columnId: string, value: string, operator?: string) => void;
  onClose: () => void;
  data: ServiceTicket[];
}> = ({ column, filter, onFilterChange, onClose, data }) => {
  const [tempValue, setTempValue] = useState<string>(filter?.value || '');
  const [tempOperator, setTempOperator] = useState<string>(filter?.operator || 'contains');

  const getUniqueValues = () => {
    if (column.filterOptions) return column.filterOptions;
    
    const values = new Set<string>();
    data.forEach(item => {
      let value;
      
      // Special handling for service workflow columns
      if (column.id === 'workflowNumber') {
        const workflowInfo = getServiceWorkflowInfo(item);
        value = workflowInfo?.workflowNumber;
      } else if (column.id === 'workflowStatus') {
        const workflowInfo = getServiceWorkflowInfo(item);
        value = workflowInfo?.status;
      } else if (column.id === 'workflowStep') {
        const workflowInfo = getServiceWorkflowInfo(item);
        value = workflowInfo?.currentStep;
      } else if (column.id === 'company') {
        value = item.customerCompany;
      } else if (column.id === 'agent') {
        value = item.agent ? item.agent.name : 'NA';
      } else if (column.id === 'customer') {
        value = item.customerName || item.customer?.name || 'Not provided';
      } else if (column.id === 'created') {
        value = item.createdAt;
      } else {
        value = getNestedValue(item, column.id);
      }
      
      if (value !== null && value !== undefined && value !== '') {
        values.add(String(value));
      }
    });
    return Array.from(values).sort().map(value => ({ value, label: value }));
  };

  const getNestedValue = (obj: any, path: string) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const handleApply = () => {
    onFilterChange(column.id, tempValue, tempOperator);
    onClose();
  };

  const handleClear = () => {
    onFilterChange(column.id, '');
    onClose();
  };

  return (
    <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 min-w-64">
      <div className="p-3">
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Filter by {column.label}
        </div>
        
        {column.filterType === 'select' ? (
          <select
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">All</option>
            {getUniqueValues().map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <>
            <select
              value={tempOperator}
              onChange={(e) => setTempOperator(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 mb-2"
            >
              <option value="contains">Contains</option>
              <option value="equals">Equals</option>
              <option value="startsWith">Starts with</option>
              <option value="endsWith">Ends with</option>
              {column.filterType === 'number' && (
                <>
                  <option value="greaterThan">Greater than</option>
                  <option value="lessThan">Less than</option>
                </>
              )}
            </select>
            <input
              type={column.filterType === 'date' ? 'date' : column.filterType === 'number' ? 'number' : 'text'}
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              placeholder={`Filter ${column.label.toLowerCase()}...`}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </>
        )}
        
        <div className="flex space-x-2 mt-3">
          <button
            onClick={handleApply}
            className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
          >
            Apply
          </button>
          <button
            onClick={handleClear}
            className="flex-1 px-3 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
          >
            Clear
          </button>
          <button
            onClick={onClose}
            className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-sm rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const SortableTableHeader: React.FC<{
  column: Column;
  onResize: (id: string, width: number) => void;
  onToggleVisibility: (id: string) => void;
  onSort?: (columnId: string) => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filter?: ColumnFilter;
  onFilterChange?: (columnId: string, value: string, operator?: string) => void;
  data?: ServiceTicket[];
}> = ({ column, onResize, onToggleVisibility, onSort, sortBy, sortOrder, filter, onFilterChange, data = [] }) => {
  const [resizing, setResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!column.resizable) return;
    setResizing(true);
    setStartX(e.clientX);
    setStartWidth(column.width);
  };

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.max(50, startWidth + (e.clientX - startX));
      onResize(column.id, newWidth);
    };

    const handleMouseUp = () => {
      setResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, startX, startWidth, column.id, onResize]);

  if (!column.visible) return null;

  const getSortIcon = () => {
    if (!column.sortable || sortBy !== column.id) {
      return column.sortable ? (
        <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      ) : null;
    }
    
    return sortOrder === 'asc' ? (
      <svg className="w-4 h-4 text-blue-600 hover:text-blue-800 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-blue-600 hover:text-blue-800 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const getFilterIcon = () => {
    const hasFilter = filter && filter.value;
    return (
      <svg 
        className={`w-4 h-4 transition-colors ${
          hasFilter ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'
        }`} 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.121A1 1 0 013 6.414V4z" />
      </svg>
    );
  };

  return (
    <th
      ref={setNodeRef}
      style={{ ...style, width: column.width }}
      className="relative bg-gray-50 dark:bg-gray-700 px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600 select-none"
      {...attributes}
    >
      <div className="flex items-center justify-between min-h-[20px]">
        <div 
          className="flex items-center space-x-2 cursor-move flex-1"
          {...listeners}
        >
          <span className="truncate">{column.label}</span>
          {column.sortable && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSort?.(column.id);
              }}
              className="flex-shrink-0"
            >
              {getSortIcon()}
            </button>
          )}
        </div>
        
        <div className="flex items-center space-x-1 ml-2">
          {column.filterable && onFilterChange && (
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFilterDropdown(!showFilterDropdown);
                }}
                className="flex-shrink-0"
              >
                {getFilterIcon()}
              </button>
              {showFilterDropdown && (
                <ColumnFilterDropdown
                  column={column}
                  filter={filter}
                  onFilterChange={onFilterChange}
                  onClose={() => setShowFilterDropdown(false)}
                  data={data}
                />
              )}
            </div>
          )}
        </div>
      </div>
      
      {column.resizable && (
        <div
          className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-300 dark:hover:bg-blue-600"
          onMouseDown={handleMouseDown}
        />
      )}
    </th>
  );
};

const ConfigurableServiceTable: React.FC<ConfigurableServiceTableProps> = (props) => {
  const { user } = useAuth();
  const [activeColumnId, setActiveColumnId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string>('created');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);

  // Create service-specific columns
  const createDefaultColumns = (): Column[] => [
    {
      id: 'ticket',
      label: 'Ticket',
      width: 200,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (ticket) => (
        <div className="w-full">
          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {ticket.title}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            #{ticket.ticketNumber || ticket.id}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300 mt-1 truncate">
            {ticket.description}
          </div>
        </div>
      ),
    },
    {
      id: 'customer',
      label: 'Customer',
      width: 120,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (ticket) => (
        <div className="flex flex-col">
          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {ticket.customerName || 'Anonymous'}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {ticket.customerCompany || ticket.customerEmail}
          </div>
        </div>
      ),
    },
    {
      id: 'deviceModel',
      label: 'Device Model',
      width: 100,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'BWIII', label: 'BWIII' },
        { value: 'BWMini', label: 'BWMini' },
        { value: 'Compass', label: 'Compass' },
        { value: 'Maxxi', label: 'Maxxi' }
      ],
      render: (ticket) => (
        <div className="text-sm text-gray-900 dark:text-white truncate">
          {ticket.deviceModel || 'N/A'}
        </div>
      ),
    },
    {
      id: 'deviceSerialNumber',
      label: 'Device S/N',
      width: 120,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (ticket) => (
        <div className="text-sm text-gray-900 dark:text-white truncate">
          {ticket.deviceSerialNumber || 'No S/N'}
        </div>
      ),
    },
    {
      id: 'workflowNumber',
      label: 'Workflow #',
      width: 120,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (ticket) => {
        const workflowInfo = getServiceWorkflowInfo(ticket);
        return (
          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {workflowInfo?.workflowNumber || 'N/A'}
          </div>
        );
      },
    },
    {
      id: 'workflowStep',
      label: 'Step',
      width: 80,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: `Step ${i + 1}` })),
      render: (ticket) => {
        const workflowInfo = getServiceWorkflowInfo(ticket);
        return (
          <div className="text-sm text-gray-900 dark:text-white text-center">
            {workflowInfo ? `${workflowInfo.currentStep}/10` : 'N/A'}
          </div>
        );
      },
    },
    {
      id: 'workflowStatus',
      label: 'Workflow Status',
      width: 120,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'in_progress', label: 'In Progress' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' }
      ],
      render: (ticket) => {
        const workflowInfo = getServiceWorkflowInfo(ticket);
        if (!workflowInfo) return <span className="text-sm text-gray-500">N/A</span>;
        
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getWorkflowStatusColor(workflowInfo.status)}`}>
            {workflowInfo.status}
          </span>
        );
      },
    },
    {
      id: 'workflowInitiated',
      label: 'Initiated',
      width: 100,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'date',
      render: (ticket) => {
        const workflowInfo = getServiceWorkflowInfo(ticket);
        return (
          <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {workflowInfo?.initiatedAt ? props.formatRelativeTime(workflowInfo.initiatedAt) : 'N/A'}
          </div>
        );
      },
    },
    {
      id: 'status',
      label: 'Ticket Status',
      width: 120,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'new', label: 'New' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'service', label: 'Service' },
        { value: 'closed-resolved', label: 'Resolved' },
        { value: 'closed-not-resolved', label: 'Not Resolved' }
      ],
      render: (ticket) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColorClasses(ticket.status as any)}`}>
          {getStatusDisplayName(ticket.status as any)}
        </span>
      ),
    },
    {
      id: 'priority',
      label: 'Priority',
      width: 80,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'critical', label: 'Critical' }
      ],
      render: (ticket) => (
        <span className={`text-sm font-medium capitalize ${props.getPriorityColor(ticket.priority)}`}>
          {ticket.priority}
        </span>
      ),
    },
    {
      id: 'agent',
      label: 'Agent',
      width: 100,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (ticket) => (
        <div className="text-sm text-gray-900 dark:text-white truncate">
          {ticket.agent ? ticket.agent.name : 'Unassigned'}
        </div>
      ),
    },
    {
      id: 'created',
      label: 'Created',
      width: 100,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'date',
      render: (ticket) => (
        <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
          {props.formatRelativeTime(ticket.createdAt)}
        </div>
      ),
    },
    // Additional service workflow columns (hidden by default)
    {
      id: 'repairDescription',
      label: 'Repair Description',
      width: 200,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (ticket) => {
        const workflows = ticket.serviceWorkflows || [];
        const workflow = workflows[0];
        const repairStep = workflow?.steps?.find((step: any) => step.step_number === 6); // Correction and Technical Report step (step 6)
        const repairDescription = repairStep?.repairDescription || repairStep?.repair_description || repairStep?.step_data?.repairDescription || repairStep?.step_data?.repair_description;
        return (
          <div className="text-sm text-gray-900 dark:text-white truncate max-w-xs" title={repairDescription}>
            {repairDescription || 'N/A'}
          </div>
        );
      },
    },
    {
      id: 'diagnosticSummary',
      label: 'Diagnostic Summary',
      width: 200,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (ticket) => {
        const workflows = ticket.serviceWorkflows || [];
        const workflow = workflows[0];
        const diagnosticStep = workflow?.steps?.find((step: any) => step.step_number === 4); // Defect Analysis step
        const diagnosticSummary = diagnosticStep?.diagnosticSummary || diagnosticStep?.diagnostic_summary || diagnosticStep?.step_data?.diagnosticSummary || diagnosticStep?.step_data?.diagnostic_summary;
        return (
          <div className="text-sm text-gray-900 dark:text-white truncate max-w-xs" title={diagnosticSummary}>
            {diagnosticSummary || 'N/A'}
          </div>
        );
      },
    },
    {
      id: 'findingsDescription',
      label: 'Findings Description',
      width: 200,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (ticket) => {
        const workflows = ticket.serviceWorkflows || [];
        const workflow = workflows[0];
        const findingsStep = workflow?.steps?.find((step: any) => step.step_number === 4); // Defect Analysis step
        const findingsDescription = findingsStep?.findingsDescription || findingsStep?.findings_description || findingsStep?.step_data?.findingsDescription || findingsStep?.step_data?.findings_description;
        return (
          <div className="text-sm text-gray-900 dark:text-white truncate max-w-xs" title={findingsDescription}>
            {findingsDescription || 'N/A'}
          </div>
        );
      },
    },
    {
      id: 'quoteNumber',
      label: 'Quote Number',
      width: 120,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (ticket) => {
        const workflows = ticket.serviceWorkflows || [];
        const workflow = workflows[0];
        const quoteStep = workflow?.steps?.find((step: any) => step.step_number === 5); // Quote & Approval step
        const quoteNumber = quoteStep?.quoteNumber || quoteStep?.quote_number || quoteStep?.step_data?.quoteNumber || quoteStep?.step_data?.quote_number;
        return (
          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {quoteNumber || 'N/A'}
          </div>
        );
      },
    },
    {
      id: 'invoiceNumber',
      label: 'Invoice Number',
      width: 120,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (ticket) => {
        const workflows = ticket.serviceWorkflows || [];
        const workflow = workflows[0];
        const invoiceStep = workflow?.steps?.find((step: any) => step.step_number === 5); // Quote & Approval step (invoice is in step 5)
        const invoiceNumber = invoiceStep?.invoiceNumber || invoiceStep?.invoice_number || invoiceStep?.step_data?.invoiceNumber || invoiceStep?.step_data?.invoice_number;
        return (
          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {invoiceNumber || 'N/A'}
          </div>
        );
      },
    },
    {
      id: 'loanerModel',
      label: 'Loaner Model',
      width: 120,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'BWIII', label: 'BWIII' },
        { value: 'BWMini', label: 'BWMini' },
        { value: 'Compass', label: 'Compass' },
        { value: 'Maxxi', label: 'Maxxi' }
      ],
      render: (ticket) => {
        const workflows = ticket.serviceWorkflows || [];
        const workflow = workflows[0];
        const loanerStep = workflow?.steps?.find((step: any) => step.step_number === 2); // Ship Loaner to Customer step
        const loanerModel = loanerStep?.loanerModel || loanerStep?.loaner_model || loanerStep?.step_data?.loanerModel || loanerStep?.step_data?.loaner_model;
        return (
          <div className="text-sm text-gray-900 dark:text-white truncate">
            {loanerModel || 'N/A'}
          </div>
        );
      },
    },
    {
      id: 'loanerSerialNumber',
      label: 'Loaner Serial #',
      width: 130,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (ticket) => {
        const workflows = ticket.serviceWorkflows || [];
        const workflow = workflows[0];
        const loanerStep = workflow?.steps?.find((step: any) => step.step_number === 2); // Ship Loaner to Customer step
        const loanerSerial = loanerStep?.loanerSerialNumber || loanerStep?.loaner_serial_number || loanerStep?.step_data?.loanerSerialNumber || loanerStep?.step_data?.loaner_serial_number;
        return (
          <div className="text-sm text-gray-900 dark:text-white truncate">
            {loanerSerial || 'N/A'}
          </div>
        );
      },
    },
    {
      id: 'partsUsed',
      label: 'Parts Used',
      width: 250,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (ticket) => {
        const workflows = ticket.serviceWorkflows || [];
        const workflow = workflows[0];
        const partsStep = workflow?.steps?.find((step: any) => step.step_number === 6); // Correction and Technical Report step
        const partsUsed = partsStep?.partsUsed || partsStep?.parts_used || partsStep?.step_data?.partsUsed || partsStep?.step_data?.parts_used;
        
        // Handle both string and array formats
        if (!partsUsed) {
          return (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              N/A
            </div>
          );
        }
        
        let partsArray: Array<{partName: string; quantity: string; lotNumber: string}> = [];
        if (typeof partsUsed === 'string') {
          // If it's a string, treat it as a single part
          partsArray = [{ partName: partsUsed, quantity: '1', lotNumber: '' }];
        } else if (Array.isArray(partsUsed)) {
          partsArray = partsUsed.map((part: any) => {
            if (typeof part === 'string') {
              return { partName: part, quantity: '1', lotNumber: '' };
            }
            return {
              partName: part.partName || part.part_name || part.description || part.partNumber || part.part_number || '',
              quantity: part.quantity || part.qty || '1',
              lotNumber: part.lotNumber || part.lot_number || part.serialNumber || part.serial_number || ''
            };
          });
        } else if (typeof partsUsed === 'object') {
          // Handle object format - convert to array
          partsArray = Object.entries(partsUsed).map(([key, value]: [string, any]) => {
            if (typeof value === 'string') {
              return { partName: value, quantity: '1', lotNumber: '' };
            }
            return {
              partName: value?.partName || value?.part_name || value?.description || key,
              quantity: value?.quantity || value?.qty || '1',
              lotNumber: value?.lotNumber || value?.lot_number || value?.serialNumber || value?.serial_number || ''
            };
          });
        }
        
        // Filter out empty parts
        const validParts = partsArray.filter(part => part.partName && part.partName.trim());
        
        if (validParts.length === 0) {
          return (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              N/A
            </div>
          );
        }
        
        // Create tooltip text
        const tooltipText = validParts.map(part => 
          `${part.partName}, ${part.quantity}${part.lotNumber ? `, ${part.lotNumber}` : ''}`
        ).join('\n');
        
        return (
          <div className="text-sm text-gray-900 dark:text-white max-w-xs" title={tooltipText}>
            {validParts.map((part, index) => (
              <div key={index} className="truncate leading-tight">
                <span className="font-medium">{part.partName}</span>
                <span className="text-gray-600 dark:text-gray-300">, {part.quantity}</span>
                {part.lotNumber && (
                  <span className="text-gray-600 dark:text-gray-300">, {part.lotNumber}</span>
                )}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      id: 'warrantyExpiration',
      label: 'Warranty Expiration',
      width: 140,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'date',
      render: (ticket) => {
        const workflows = ticket.serviceWorkflows || [];
        const workflow = workflows[0];
        const requestStep = workflow?.steps?.find((step: any) => step.step_number === 1); // Request Device for Repair step
        const warrantyExpiration = requestStep?.warrantyExpiration || requestStep?.warranty_expiration || requestStep?.step_data?.warrantyExpiration || requestStep?.step_data?.warranty_expiration;
        
        if (!warrantyExpiration) {
          return (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              N/A
            </div>
          );
        }
        
        const expirationDate = new Date(warrantyExpiration);
        const formattedDate = expirationDate.toLocaleDateString();
        
        return (
          <div className="text-sm text-gray-900 dark:text-white">
            {formattedDate}
          </div>
        );
      },
    },
    {
      id: 'warrantyStatus',
      label: 'Warranty Status',
      width: 120,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'current', label: 'Current' },
        { value: 'expired', label: 'Expired' },
        { value: 'unknown', label: 'Unknown' }
      ],
      render: (ticket) => {
        const workflows = ticket.serviceWorkflows || [];
        const workflow = workflows[0];
        const requestStep = workflow?.steps?.find((step: any) => step.step_number === 1); // Request Device for Repair step
        const warrantyExpiration = requestStep?.warrantyExpiration || requestStep?.warranty_expiration || requestStep?.step_data?.warrantyExpiration || requestStep?.step_data?.warranty_expiration;
        
        if (!warrantyExpiration) {
          return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
              Unknown
            </span>
          );
        }
        
        const expirationDate = new Date(warrantyExpiration);
        const currentDate = new Date();
        const isExpired = expirationDate < currentDate;
        
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            isExpired 
              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' 
              : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
          }`}>
            {isExpired ? 'Expired' : 'Current'}
          </span>
        );
      },
    },
    {
      id: 'workflowCompleted',
      label: 'Completed',
      width: 100,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'date',
      render: (ticket) => {
        const workflowInfo = getServiceWorkflowInfo(ticket);
        return (
          <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {workflowInfo?.completedAt ? props.formatRelativeTime(workflowInfo.completedAt) : 'N/A'}
          </div>
        );
      },
    },
    {
      id: 'workflowCancelled',
      label: 'Cancelled',
      width: 100,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'date',
      render: (ticket) => {
        const workflowInfo = getServiceWorkflowInfo(ticket);
        return (
          <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {workflowInfo?.cancelledAt ? props.formatRelativeTime(workflowInfo.cancelledAt) : 'N/A'}
          </div>
        );
      },
    },
    {
      id: 'customerEmail',
      label: 'Email',
      width: 150,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (ticket) => (
        <div className="text-sm text-gray-900 dark:text-white truncate">
          {ticket.customerEmail || 'Not provided'}
        </div>
      ),
    },
    {
      id: 'customerPhone',
      label: 'Phone',
      width: 120,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (ticket) => (
        <div className="text-sm text-gray-900 dark:text-white truncate">
          {ticket.customerPhone || 'Not provided'}
        </div>
      ),
    },
    {
      id: 'customerType',
      label: 'Customer Type',
      width: 120,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'VIP', label: 'VIP' },
        { value: 'Distributor', label: 'Distributor' },
        { value: 'Standard', label: 'Standard' }
      ],
      render: (ticket) => (
        <div className="text-sm text-gray-900 dark:text-white truncate">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            ticket.customerType === 'VIP' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
            ticket.customerType === 'Distributor' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
          }`}>
            {ticket.customerType || 'Standard'}
          </span>
        </div>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      width: 120,
      visible: true,
      resizable: false,
      sortable: false,
      render: (ticket) => (
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => props.onTicketSelect(ticket)}
            className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
            title="View ticket"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          {user?.permissions?.includes('tickets.edit') && (
            <button
              onClick={() => props.onTicketSelect(ticket)}
              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
              title="Edit ticket"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}
          {user?.permissions?.includes('tickets.edit') && isTicketOpen(ticket.status as any) && props.onReassignTicket && (
            <div className="relative">
              {props.reassignTicketId === ticket.id ? (
                <select
                  onChange={(e) => {
                    if (e.target.value !== ticket.agentId) {
                      if (e.target.value === 'unassign') {
                        props.onReassignTicket!(ticket.id, '');
                      } else if (e.target.value) {
                        props.onReassignTicket!(ticket.id, e.target.value);
                      } else {
                        props.setReassignTicketId(null);
                      }
                    } else {
                      props.setReassignTicketId(null);
                    }
                  }}
                  onBlur={() => props.setReassignTicketId(null)}
                  className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  disabled={props.reassigning}
                  defaultValue=""
                  autoFocus
                >
                  <option value="">Cancel</option>
                  {ticket.agentId && (
                    <option value="unassign">⊘ Unassign</option>
                  )}
                  {props.agents.map(agent => {
                    const isOnlineFromAgent = agent.isOnline || false;
                    const isOnlineFromPresence = props.agentPresence?.get(agent.id) || false;
                    const isOnline = isOnlineFromAgent || isOnlineFromPresence;
                    const statusIndicator = isOnline ? '🟢' : '🔴';
                    
                    return (
                      <option key={agent.id} value={agent.id}>
                        {statusIndicator} {agent.roleName} - {agent.name}
                        {agent.id === ticket.agentId ? ' (Current)' : ''}
                        {agent.id === user?.id ? ' (You)' : ''}
                        {isOnline ? ' (Online)' : ' (Offline)'}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    props.setReassignTicketId(ticket.id);
                  }}
                  className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300 font-medium"
                  title={ticket.agentId ? "Reassign ticket" : "Assign ticket"}
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </button>
              )}
            </div>
          )}
          {user?.permissions?.includes('tickets.delete') && props.onDeleteTicket && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                props.onDeleteTicket!(ticket.id);
              }}
              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 font-medium"
              title="Delete ticket"
              disabled={props.deletingTicketId === ticket.id}
            >
              {props.deletingTicketId === ticket.id ? (
                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              ) : (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              )}
            </button>
          )}
        </div>
      ),
    },
  ];

  // Load preferences from localStorage
  const loadPreferences = (): TablePreferences => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const defaultColumns = createDefaultColumns();
        
        // Use saved column visibility preferences
        const updatedVisibility = {
          ...parsed.columnVisibility,
        };
        
        let updatedColumnOrder = parsed.columnOrder || defaultColumns.map(c => c.id);
        
        return {
          columnOrder: updatedColumnOrder,
          columnWidths: parsed.columnWidths || {},
          columnVisibility: updatedVisibility,
          pagination: (parsed.pagination && typeof parsed.pagination === 'object') ? {
            itemsPerPage: parsed.pagination.itemsPerPage || 50,
            currentPage: parsed.pagination.currentPage || 1,
          } : { itemsPerPage: 50, currentPage: 1 },
        };
      }
    } catch (error) {
      console.error('Error loading table preferences:', error);
    }
    const defaultColumns = createDefaultColumns();
    return {
      columnOrder: defaultColumns.map(c => c.id),
      columnWidths: {},
      columnVisibility: {},
      pagination: {
        itemsPerPage: 50,
        currentPage: 1,
      },
    };
  };

  // Save preferences to localStorage
  const savePreferences = (preferences: TablePreferences) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving table preferences:', error);
    }
  };

  const [preferences, setPreferences] = useState<TablePreferences>(loadPreferences);
  const defaultColumns = createDefaultColumns();

  // Apply preferences to columns
  const configuredColumns = useMemo(() => {
    const columnsMap = new Map(defaultColumns.map(col => [col.id, col]));
    
    return preferences.columnOrder.map(columnId => {
      const column = columnsMap.get(columnId);
      if (!column) return null;
      
      const isVisible = preferences.columnVisibility[columnId] !== false;
      
      return {
        ...column,
        width: preferences.columnWidths[columnId] || column.width,
        visible: isVisible,
      };
    }).filter(Boolean) as Column[];
  }, [preferences, defaultColumns]);

  const visibleColumns = configuredColumns.filter(col => col.visible);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveColumnId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveColumnId(null);

    if (active.id !== over?.id) {
      const oldIndex = preferences.columnOrder.indexOf(active.id as string);
      const newIndex = preferences.columnOrder.indexOf(over?.id as string);
      
      const newColumnOrder = arrayMove(preferences.columnOrder, oldIndex, newIndex);
      const newPreferences = { ...preferences, columnOrder: newColumnOrder };
      setPreferences(newPreferences);
      savePreferences(newPreferences);
    }
  };

  const handleColumnResize = (columnId: string, width: number) => {
    const newPreferences = {
      ...preferences,
      columnWidths: { ...preferences.columnWidths, [columnId]: width },
    };
    setPreferences(newPreferences);
    savePreferences(newPreferences);
  };

  const handleColumnVisibilityToggle = (columnId: string) => {
    const newPreferences = {
      ...preferences,
      columnVisibility: {
        ...preferences.columnVisibility,
        [columnId]: !preferences.columnVisibility[columnId],
      },
    };
    setPreferences(newPreferences);
    savePreferences(newPreferences);
  };

  const handleResetColumns = () => {
    const defaultColumns = createDefaultColumns();
    const defaultPreferences: TablePreferences = {
      columnOrder: defaultColumns.map(c => c.id),
      columnWidths: {},
      columnVisibility: {},
      pagination: {
        itemsPerPage: 50,
        currentPage: 1,
      },
    };
    setPreferences(defaultPreferences);
    savePreferences(defaultPreferences);
  };

  const handleColumnFilterChange = (columnId: string, value: string, operator: string = 'contains') => {
    const newFilters = columnFilters.filter(f => f.columnId !== columnId);
    if (value) {
      newFilters.push({ columnId, value, operator: operator as any });
    }
    setColumnFilters(newFilters);
  };

  const applyColumnFilters = (tickets: ServiceTicket[]): ServiceTicket[] => {
    if (!columnFilters.length) return tickets;
    
    return tickets.filter(ticket => {
      return columnFilters.every(filter => {
        let value;
        
        // Special handling for service workflow columns
        if (filter.columnId === 'workflowNumber') {
          const workflowInfo = getServiceWorkflowInfo(ticket);
          value = workflowInfo?.workflowNumber || '';
        } else if (filter.columnId === 'workflowStatus') {
          const workflowInfo = getServiceWorkflowInfo(ticket);
          value = workflowInfo?.status || '';
        } else if (filter.columnId === 'workflowStep') {
          const workflowInfo = getServiceWorkflowInfo(ticket);
          value = workflowInfo?.currentStep?.toString() || '';
        } else if (filter.columnId === 'repairDescription') {
          const workflows = ticket.serviceWorkflows || [];
          const workflow = workflows[0];
          const repairStep = workflow?.steps?.find((step: any) => step.step_number === 6);
          value = repairStep?.repairDescription || repairStep?.repair_description || repairStep?.step_data?.repairDescription || repairStep?.step_data?.repair_description || '';
        } else if (filter.columnId === 'diagnosticSummary') {
          const workflows = ticket.serviceWorkflows || [];
          const workflow = workflows[0];
          const diagnosticStep = workflow?.steps?.find((step: any) => step.step_number === 4);
          value = diagnosticStep?.diagnosticSummary || diagnosticStep?.diagnostic_summary || diagnosticStep?.step_data?.diagnosticSummary || diagnosticStep?.step_data?.diagnostic_summary || '';
        } else if (filter.columnId === 'findingsDescription') {
          const workflows = ticket.serviceWorkflows || [];
          const workflow = workflows[0];
          const findingsStep = workflow?.steps?.find((step: any) => step.step_number === 4);
          value = findingsStep?.findingsDescription || findingsStep?.findings_description || findingsStep?.step_data?.findingsDescription || findingsStep?.step_data?.findings_description || '';
        } else if (filter.columnId === 'quoteNumber') {
          const workflows = ticket.serviceWorkflows || [];
          const workflow = workflows[0];
          const quoteStep = workflow?.steps?.find((step: any) => step.step_number === 5);
          value = quoteStep?.quoteNumber || quoteStep?.quote_number || quoteStep?.step_data?.quoteNumber || quoteStep?.step_data?.quote_number || '';
        } else if (filter.columnId === 'invoiceNumber') {
          const workflows = ticket.serviceWorkflows || [];
          const workflow = workflows[0];
          const invoiceStep = workflow?.steps?.find((step: any) => step.step_number === 5);
          value = invoiceStep?.invoiceNumber || invoiceStep?.invoice_number || invoiceStep?.step_data?.invoiceNumber || invoiceStep?.step_data?.invoice_number || '';
        } else if (filter.columnId === 'loanerModel') {
          const workflows = ticket.serviceWorkflows || [];
          const workflow = workflows[0];
          const loanerStep = workflow?.steps?.find((step: any) => step.step_number === 2);
          value = loanerStep?.loanerModel || loanerStep?.loaner_model || loanerStep?.step_data?.loanerModel || loanerStep?.step_data?.loaner_model || '';
        } else if (filter.columnId === 'loanerSerialNumber') {
          const workflows = ticket.serviceWorkflows || [];
          const workflow = workflows[0];
          const loanerStep = workflow?.steps?.find((step: any) => step.step_number === 2);
          value = loanerStep?.loanerSerialNumber || loanerStep?.loaner_serial_number || loanerStep?.step_data?.loanerSerialNumber || loanerStep?.step_data?.loaner_serial_number || '';
        } else if (filter.columnId === 'partsUsed') {
          const workflows = ticket.serviceWorkflows || [];
          const workflow = workflows[0];
          const partsStep = workflow?.steps?.find((step: any) => step.step_number === 6);
          const partsUsed = partsStep?.partsUsed || partsStep?.parts_used || partsStep?.step_data?.partsUsed || partsStep?.step_data?.parts_used;
          
          if (partsUsed) {
            let partsArray: Array<{partName: string; quantity: string; lotNumber: string}> = [];
            if (typeof partsUsed === 'string') {
              partsArray = [{ partName: partsUsed, quantity: '1', lotNumber: '' }];
            } else if (Array.isArray(partsUsed)) {
              partsArray = partsUsed.map((part: any) => {
                if (typeof part === 'string') {
                  return { partName: part, quantity: '1', lotNumber: '' };
                }
                return {
                  partName: part.partName || part.part_name || part.description || part.partNumber || part.part_number || '',
                  quantity: part.quantity || part.qty || '1',
                  lotNumber: part.lotNumber || part.lot_number || part.serialNumber || part.serial_number || ''
                };
              });
            } else if (typeof partsUsed === 'object') {
              partsArray = Object.entries(partsUsed).map(([key, val]: [string, any]) => {
                if (typeof val === 'string') {
                  return { partName: val, quantity: '1', lotNumber: '' };
                }
                return {
                  partName: val?.partName || val?.part_name || val?.description || key,
                  quantity: val?.quantity || val?.qty || '1',
                  lotNumber: val?.lotNumber || val?.lot_number || val?.serialNumber || val?.serial_number || ''
                };
              });
            }
            
            // Create searchable text with all part information
            value = partsArray.map(part => 
              `${part.partName} ${part.quantity} ${part.lotNumber}`.trim()
            ).filter(Boolean).join(' ');
          } else {
            value = '';
          }
        } else if (filter.columnId === 'warrantyExpiration') {
          const workflows = ticket.serviceWorkflows || [];
          const workflow = workflows[0];
          const requestStep = workflow?.steps?.find((step: any) => step.step_number === 1);
          const warrantyExpiration = requestStep?.warrantyExpiration || requestStep?.warranty_expiration || requestStep?.step_data?.warrantyExpiration || requestStep?.step_data?.warranty_expiration;
          value = warrantyExpiration || '';
        } else if (filter.columnId === 'warrantyStatus') {
          const workflows = ticket.serviceWorkflows || [];
          const workflow = workflows[0];
          const requestStep = workflow?.steps?.find((step: any) => step.step_number === 1);
          const warrantyExpiration = requestStep?.warrantyExpiration || requestStep?.warranty_expiration || requestStep?.step_data?.warrantyExpiration || requestStep?.step_data?.warranty_expiration;
          
          if (!warrantyExpiration) {
            value = 'unknown';
          } else {
            const expirationDate = new Date(warrantyExpiration);
            const currentDate = new Date();
            const isExpired = expirationDate < currentDate;
            value = isExpired ? 'expired' : 'current';
          }
        } else if (filter.columnId === 'company') {
          value = ticket.customerCompany || '';
        } else if (filter.columnId === 'agent') {
          value = ticket.agent ? ticket.agent.name : 'NA';
        } else if (filter.columnId === 'customer') {
          value = ticket.customerName || ticket.customer?.name || 'Not provided';
        } else if (filter.columnId === 'created') {
          value = ticket.createdAt;
        } else {
          value = getNestedValue(ticket, filter.columnId);
        }
        
        if (value === null || value === undefined) value = '';
        const stringValue = String(value).toLowerCase();
        const filterValue = filter.value.toLowerCase();
        
        switch (filter.operator) {
          case 'equals':
            return stringValue === filterValue;
          case 'startsWith':
            return stringValue.startsWith(filterValue);
          case 'endsWith':
            return stringValue.endsWith(filterValue);
          case 'greaterThan':
            return parseFloat(stringValue) > parseFloat(filterValue);
          case 'lessThan':
            return parseFloat(stringValue) < parseFloat(filterValue);
          case 'contains':
          default:
            return stringValue.includes(filterValue);
        }
      });
    });
  };

  const getNestedValue = (obj: any, path: string) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const handleSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnId);
      setSortOrder('asc');
    }
  };

  const sortedAndFilteredTickets = useMemo(() => {
    let result = applyColumnFilters(props.tickets);
    
    if (sortBy) {
      result = [...result].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        
        // Special handling for service workflow columns
        if (sortBy === 'workflowNumber') {
          const aWorkflow = getServiceWorkflowInfo(a);
          const bWorkflow = getServiceWorkflowInfo(b);
          aValue = aWorkflow?.workflowNumber || '';
          bValue = bWorkflow?.workflowNumber || '';
        } else if (sortBy === 'workflowStatus') {
          const aWorkflow = getServiceWorkflowInfo(a);
          const bWorkflow = getServiceWorkflowInfo(b);
          aValue = aWorkflow?.status || '';
          bValue = bWorkflow?.status || '';
        } else if (sortBy === 'workflowStep') {
          const aWorkflow = getServiceWorkflowInfo(a);
          const bWorkflow = getServiceWorkflowInfo(b);
          aValue = aWorkflow?.currentStep || 0;
          bValue = bWorkflow?.currentStep || 0;
        } else if (sortBy === 'workflowInitiated') {
          const aWorkflow = getServiceWorkflowInfo(a);
          const bWorkflow = getServiceWorkflowInfo(b);
          aValue = aWorkflow?.initiatedAt || '';
          bValue = bWorkflow?.initiatedAt || '';
        } else if (sortBy === 'repairDescription') {
          const aWorkflows = a.serviceWorkflows || [];
          const bWorkflows = b.serviceWorkflows || [];
          const aWorkflow = aWorkflows[0];
          const bWorkflow = bWorkflows[0];
          const aRepairStep = aWorkflow?.steps?.find((step: any) => step.step_number === 6);
          const bRepairStep = bWorkflow?.steps?.find((step: any) => step.step_number === 6);
          aValue = aRepairStep?.repairDescription || aRepairStep?.repair_description || aRepairStep?.step_data?.repairDescription || aRepairStep?.step_data?.repair_description || '';
          bValue = bRepairStep?.repairDescription || bRepairStep?.repair_description || bRepairStep?.step_data?.repairDescription || bRepairStep?.step_data?.repair_description || '';
        } else if (sortBy === 'diagnosticSummary') {
          const aWorkflows = a.serviceWorkflows || [];
          const bWorkflows = b.serviceWorkflows || [];
          const aWorkflow = aWorkflows[0];
          const bWorkflow = bWorkflows[0];
          const aDiagnosticStep = aWorkflow?.steps?.find((step: any) => step.step_number === 4);
          const bDiagnosticStep = bWorkflow?.steps?.find((step: any) => step.step_number === 4);
          aValue = aDiagnosticStep?.diagnosticSummary || aDiagnosticStep?.diagnostic_summary || aDiagnosticStep?.step_data?.diagnosticSummary || aDiagnosticStep?.step_data?.diagnostic_summary || '';
          bValue = bDiagnosticStep?.diagnosticSummary || bDiagnosticStep?.diagnostic_summary || bDiagnosticStep?.step_data?.diagnosticSummary || bDiagnosticStep?.step_data?.diagnostic_summary || '';
        } else if (sortBy === 'findingsDescription') {
          const aWorkflows = a.serviceWorkflows || [];
          const bWorkflows = b.serviceWorkflows || [];
          const aWorkflow = aWorkflows[0];
          const bWorkflow = bWorkflows[0];
          const aFindingsStep = aWorkflow?.steps?.find((step: any) => step.step_number === 4);
          const bFindingsStep = bWorkflow?.steps?.find((step: any) => step.step_number === 4);
          aValue = aFindingsStep?.findingsDescription || aFindingsStep?.findings_description || aFindingsStep?.step_data?.findingsDescription || aFindingsStep?.step_data?.findings_description || '';
          bValue = bFindingsStep?.findingsDescription || bFindingsStep?.findings_description || bFindingsStep?.step_data?.findingsDescription || bFindingsStep?.step_data?.findings_description || '';
        } else if (sortBy === 'quoteNumber') {
          const aWorkflows = a.serviceWorkflows || [];
          const bWorkflows = b.serviceWorkflows || [];
          const aWorkflow = aWorkflows[0];
          const bWorkflow = bWorkflows[0];
          const aQuoteStep = aWorkflow?.steps?.find((step: any) => step.step_number === 5);
          const bQuoteStep = bWorkflow?.steps?.find((step: any) => step.step_number === 5);
          aValue = aQuoteStep?.quoteNumber || aQuoteStep?.quote_number || aQuoteStep?.step_data?.quoteNumber || aQuoteStep?.step_data?.quote_number || '';
          bValue = bQuoteStep?.quoteNumber || bQuoteStep?.quote_number || bQuoteStep?.step_data?.quoteNumber || bQuoteStep?.step_data?.quote_number || '';
        } else if (sortBy === 'invoiceNumber') {
          const aWorkflows = a.serviceWorkflows || [];
          const bWorkflows = b.serviceWorkflows || [];
          const aWorkflow = aWorkflows[0];
          const bWorkflow = bWorkflows[0];
          const aInvoiceStep = aWorkflow?.steps?.find((step: any) => step.step_number === 5);
          const bInvoiceStep = bWorkflow?.steps?.find((step: any) => step.step_number === 5);
          aValue = aInvoiceStep?.invoiceNumber || aInvoiceStep?.invoice_number || aInvoiceStep?.step_data?.invoiceNumber || aInvoiceStep?.step_data?.invoice_number || '';
          bValue = bInvoiceStep?.invoiceNumber || bInvoiceStep?.invoice_number || bInvoiceStep?.step_data?.invoiceNumber || bInvoiceStep?.step_data?.invoice_number || '';
        } else if (sortBy === 'loanerModel') {
          const aWorkflows = a.serviceWorkflows || [];
          const bWorkflows = b.serviceWorkflows || [];
          const aWorkflow = aWorkflows[0];
          const bWorkflow = bWorkflows[0];
          const aLoanerStep = aWorkflow?.steps?.find((step: any) => step.step_number === 2);
          const bLoanerStep = bWorkflow?.steps?.find((step: any) => step.step_number === 2);
          aValue = aLoanerStep?.loanerModel || aLoanerStep?.loaner_model || aLoanerStep?.step_data?.loanerModel || aLoanerStep?.step_data?.loaner_model || '';
          bValue = bLoanerStep?.loanerModel || bLoanerStep?.loaner_model || bLoanerStep?.step_data?.loanerModel || bLoanerStep?.step_data?.loaner_model || '';
        } else if (sortBy === 'loanerSerialNumber') {
          const aWorkflows = a.serviceWorkflows || [];
          const bWorkflows = b.serviceWorkflows || [];
          const aWorkflow = aWorkflows[0];
          const bWorkflow = bWorkflows[0];
          const aLoanerStep = aWorkflow?.steps?.find((step: any) => step.step_number === 2);
          const bLoanerStep = bWorkflow?.steps?.find((step: any) => step.step_number === 2);
          aValue = aLoanerStep?.loanerSerialNumber || aLoanerStep?.loaner_serial_number || aLoanerStep?.step_data?.loanerSerialNumber || aLoanerStep?.step_data?.loaner_serial_number || '';
          bValue = bLoanerStep?.loanerSerialNumber || bLoanerStep?.loaner_serial_number || bLoanerStep?.step_data?.loanerSerialNumber || bLoanerStep?.step_data?.loaner_serial_number || '';
        } else if (sortBy === 'partsUsed') {
          const aWorkflows = a.serviceWorkflows || [];
          const bWorkflows = b.serviceWorkflows || [];
          const aWorkflow = aWorkflows[0];
          const bWorkflow = bWorkflows[0];
          const aPartsStep = aWorkflow?.steps?.find((step: any) => step.step_number === 6);
          const bPartsStep = bWorkflow?.steps?.find((step: any) => step.step_number === 6);
          const aPartsUsed = aPartsStep?.partsUsed || aPartsStep?.parts_used || aPartsStep?.step_data?.partsUsed || aPartsStep?.step_data?.parts_used;
          const bPartsUsed = bPartsStep?.partsUsed || bPartsStep?.parts_used || bPartsStep?.step_data?.partsUsed || bPartsStep?.step_data?.parts_used;
          
          // Convert parts to string for comparison
          const convertPartsToString = (parts: any) => {
            if (!parts) return '';
            
            let partsArray: Array<{partName: string; quantity: string; lotNumber: string}> = [];
            if (typeof parts === 'string') {
              partsArray = [{ partName: parts, quantity: '1', lotNumber: '' }];
            } else if (Array.isArray(parts)) {
              partsArray = parts.map((part: any) => {
                if (typeof part === 'string') {
                  return { partName: part, quantity: '1', lotNumber: '' };
                }
                return {
                  partName: part.partName || part.part_name || part.description || part.partNumber || part.part_number || '',
                  quantity: part.quantity || part.qty || '1',
                  lotNumber: part.lotNumber || part.lot_number || part.serialNumber || part.serial_number || ''
                };
              });
            } else if (typeof parts === 'object') {
              partsArray = Object.entries(parts).map(([key, val]: [string, any]) => {
                if (typeof val === 'string') {
                  return { partName: val, quantity: '1', lotNumber: '' };
                }
                return {
                  partName: val?.partName || val?.part_name || val?.description || key,
                  quantity: val?.quantity || val?.qty || '1',
                  lotNumber: val?.lotNumber || val?.lot_number || val?.serialNumber || val?.serial_number || ''
                };
              });
            }
            
            // Create sortable text - primarily by part name
            return partsArray.map(part => part.partName).filter(Boolean).join(' ');
          };
          
          aValue = convertPartsToString(aPartsUsed);
          bValue = convertPartsToString(bPartsUsed);
        } else if (sortBy === 'warrantyExpiration') {
          const aWorkflows = a.serviceWorkflows || [];
          const bWorkflows = b.serviceWorkflows || [];
          const aWorkflow = aWorkflows[0];
          const bWorkflow = bWorkflows[0];
          const aRequestStep = aWorkflow?.steps?.find((step: any) => step.step_number === 1);
          const bRequestStep = bWorkflow?.steps?.find((step: any) => step.step_number === 1);
          const aWarrantyExpiration = aRequestStep?.warrantyExpiration || aRequestStep?.warranty_expiration || aRequestStep?.step_data?.warrantyExpiration || aRequestStep?.step_data?.warranty_expiration;
          const bWarrantyExpiration = bRequestStep?.warrantyExpiration || bRequestStep?.warranty_expiration || bRequestStep?.step_data?.warrantyExpiration || bRequestStep?.step_data?.warranty_expiration;
          aValue = aWarrantyExpiration || '';
          bValue = bWarrantyExpiration || '';
        } else if (sortBy === 'warrantyStatus') {
          const aWorkflows = a.serviceWorkflows || [];
          const bWorkflows = b.serviceWorkflows || [];
          const aWorkflow = aWorkflows[0];
          const bWorkflow = bWorkflows[0];
          const aRequestStep = aWorkflow?.steps?.find((step: any) => step.step_number === 1);
          const bRequestStep = bWorkflow?.steps?.find((step: any) => step.step_number === 1);
          const aWarrantyExpiration = aRequestStep?.warrantyExpiration || aRequestStep?.warranty_expiration || aRequestStep?.step_data?.warrantyExpiration || aRequestStep?.step_data?.warranty_expiration;
          const bWarrantyExpiration = bRequestStep?.warrantyExpiration || bRequestStep?.warranty_expiration || bRequestStep?.step_data?.warrantyExpiration || bRequestStep?.step_data?.warranty_expiration;
          
          // Convert warranty expiration to status for comparison
          const getWarrantyStatus = (warrantyExpiration: string) => {
            if (!warrantyExpiration) return 'unknown';
            const expirationDate = new Date(warrantyExpiration);
            const currentDate = new Date();
            const isExpired = expirationDate < currentDate;
            return isExpired ? 'expired' : 'current';
          };
          
          aValue = getWarrantyStatus(aWarrantyExpiration);
          bValue = getWarrantyStatus(bWarrantyExpiration);
        } else if (sortBy === 'customer') {
          aValue = a.customerName || a.customer?.name || '';
          bValue = b.customerName || b.customer?.name || '';
        } else if (sortBy === 'agent') {
          aValue = a.agent ? a.agent.name : '';
          bValue = b.agent ? b.agent.name : '';
        } else {
          aValue = getNestedValue(a, sortBy);
          bValue = getNestedValue(b, sortBy);
        }
        
        // Handle different data types
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue);
          return sortOrder === 'asc' ? comparison : -comparison;
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
        } else {
          // Fallback to string comparison
          const aStr = String(aValue || '');
          const bStr = String(bValue || '');
          const comparison = aStr.localeCompare(bStr);
          return sortOrder === 'asc' ? comparison : -comparison;
        }
      });
    }
    
    return result;
  }, [props.tickets, columnFilters, sortBy, sortOrder]);

  const handleExportToExcel = async () => {
    try {
      const filename = generateExportFilename({
        ...props.exportFilters,
        currentView: 'service-tickets'
      });
      
      exportTicketsToExcel({
        tickets: props.tickets as any,
        visibleColumns: visibleColumns.map(col => ({
          id: col.id,
          label: col.label,
          visible: col.visible,
          render: col.render as any
        })) as any,
        filename,
        sheetName: 'Service Tickets',
        filters: {
          ...props.exportFilters,
          currentView: 'service-tickets'
        },
        getLastCustomerMessage: props.getLastCustomerMessage as any,
        getLastAgentMessage: props.getLastAgentMessage as any,
        formatRelativeTime: props.formatRelativeTime
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
      {/* Table Controls */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Service Tickets Table
            </h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {sortedAndFilteredTickets.length} tickets
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleExportToExcel}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              <svg className="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export
            </button>
            
            {/* Column Visibility Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowColumnDropdown(!showColumnDropdown)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                <svg className="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2z" />
                </svg>
                Columns
              </button>
              
              {showColumnDropdown && (
                <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50">
                  <div className="p-3">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Show/Hide Columns
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {configuredColumns.map(column => (
                        <label key={column.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={column.visible}
                            onChange={() => handleColumnVisibilityToggle(column.id)}
                            className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:bg-gray-700"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            {column.label}
                          </span>
                        </label>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                      <button
                        onClick={handleResetColumns}
                        className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        Reset to Default
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <SortableContext items={visibleColumns.map(col => col.id)} strategy={horizontalListSortingStrategy}>
                  {visibleColumns.map(column => (
                    <SortableTableHeader
                      key={column.id}
                      column={column}
                      onResize={handleColumnResize}
                      onToggleVisibility={handleColumnVisibilityToggle}
                      onSort={handleSort}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      filter={columnFilters.find(f => f.columnId === column.id)}
                      onFilterChange={handleColumnFilterChange}
                      data={props.tickets}
                    />
                  ))}
                </SortableContext>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {sortedAndFilteredTickets.map((ticket) => (
                <tr
                  key={ticket.id}
                  onClick={() => props.onTicketSelect(ticket)}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-150"
                  style={props.getRowStyle(ticket.id)}
                >
                  {visibleColumns.map(column => (
                    <td
                      key={column.id}
                      className="px-3 py-4 whitespace-nowrap text-sm border-r border-gray-200 dark:border-gray-600 last:border-r-0"
                      style={{ width: column.width }}
                    >
                      {column.render(ticket)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </DndContext>
      </div>

      <DragOverlay>
        {activeColumnId ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded shadow-lg px-3 py-2">
            {configuredColumns.find(col => col.id === activeColumnId)?.label}
          </div>
        ) : null}
      </DragOverlay>
    </div>
  );
};

export default ConfigurableServiceTable;
